import { useCallback, useRef } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { useUploadImageWithCheck } from '~domains/profile/entities/kyc';
import {
  selfieSchema,
  type SelfieFormValues,
} from '~domains/profile/entities/person';

import { ampli } from '~shared/analytics';
import { FileUpload, type FileUploadRef } from '~shared/ui/file-upload/rhf';
import { FooterWithProgressBar } from '~shared/ui/footer-with-progress-bar';
import { Screen } from '~shared/ui/screen';

import { toast } from '@breezeventures/ui-kit/toast';
import { Typography } from '@breezeventures/ui-kit/typography';
import { zodResolver } from '@hookform/resolvers/zod';

import { PersonFormHeader } from '../../../person-form-header';

const CLIENT_PORTRAIT_DOC_TYPE = 'CLIENT_PORTRAIT';

type SelfieUploadProps = {
  initialValues?: Partial<SelfieFormValues>;
  onBack?: () => void;
  onSubmit: (values: SelfieFormValues) => void;
};

export const SelfieUpload = ({
  initialValues,
  onBack,
  onSubmit,
}: SelfieUploadProps) => {
  const fileUploadRef = useRef<FileUploadRef>(null);
  const methods = useForm<SelfieFormValues>({
    resolver: zodResolver(selfieSchema),
    mode: 'all',
    defaultValues: initialValues ?? {
      document: undefined,
    },
  });

  const { uploadWithCheck } = useUploadImageWithCheck();

  const handleUpload = useCallback(
    async (
      request: { document: File },
      options?: { signal?: AbortSignal }
    ): Promise<{ documentId: string; fileName?: string }> => {
      const { transactionId } = await uploadWithCheck({
        fileItem: {
          frontImage: request.document,
          docType: CLIENT_PORTRAIT_DOC_TYPE,
        },
        signal: options?.signal,
      });

      ampli.mpProfileSelfieUploaded();
      return {
        documentId: transactionId,
        fileName: request.document.name,
      };
    },
    [uploadWithCheck]
  );

  const handleSubmit = (values: SelfieFormValues) => {
    if (fileUploadRef.current?.checkIsUploading()) {
      toast('Still uploading, boss!', { type: 'info' });
      return;
    }
    onSubmit(values);
  };

  return (
    <form
      onSubmit={methods.handleSubmit(handleSubmit)}
      className="flex h-full flex-col"
    >
      <Screen
        header={<PersonFormHeader onBack={onBack} />}
        footer={
          <FooterWithProgressBar
            currentStepIndex={3}
            totalSteps={4}
            submitButtonText="Save & Next"
            isLoading={methods.formState.isSubmitting}
            onSubmit={methods.handleSubmit(handleSubmit)}
          />
        }
      >
        <FormProvider {...methods}>
          <div className="flex flex-col gap-3 py-6">
            <Typography.Title size="s">Upload your selfie</Typography.Title>
            <Typography.Text color="secondary">
              The&nbsp;photo must&nbsp;be&nbsp;clear. Avoid cropping parts
              of&nbsp;the&nbsp;document.
            </Typography.Text>
          </div>

          <Controller
            name="document"
            control={methods.control}
            render={(fieldProps) => (
              <FileUpload
                ref={fileUploadRef}
                {...fieldProps}
                title="Selfie"
                options={{
                  allowedExtensions: ['png', 'jpg', 'jpeg'],
                  showProgress: false,
                  capture: true,
                }}
                onUpload={handleUpload}
              />
            )}
          />
        </FormProvider>
      </Screen>
    </form>
  );
};
